import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '@wix/yoshi-flow-editor';

import { AllowPolicy } from '@wix/ambassador-social-groups-v2-group/types';

import {
  RadioButtonGroup,
  type RadioButtonGroupProps,
} from 'wui/RadioButtonGroup';
import { RadioButton } from 'wui/RadioButton';
import { Typography } from 'wui/Typography';
import { Box } from 'wui/Box';

import { selectIsSiteAdmin } from 'store/selectors';

interface IProps extends RadioButtonGroupProps {
  disabledValues?: AllowPolicy[];
  helperText?: string;
}

export function AllowPolicyField(props: IProps) {
  const { disabledValues, helperText, label, value, ...rest } = props;

  const { t } = useTranslation();
  const isSiteAdmin = useSelector(selectIsSiteAdmin);
  const isOwnerOnlyLocked = value === AllowPolicy.OWNER && !isSiteAdmin;
  const ownerOnlyHelperText = t(
    'groups-web.group.settings.permissions-form.disabled.owner-only',
  );
  const labelContent = renderLabelContent(label, helperText);
  const ownerOnlyLabelContent = renderLabelContent(
    label,
    ownerOnlyHelperText,
    `${t('groups-web.group.settings.permissions-form.current-setting')}: ${t(
      'groups-web.group.settings.permissions-form.policy.owner-only',
    )}`,
  );

  if (isOwnerOnlyLocked) {
    return ownerOnlyLabelContent;
  }

  return (
    <>
      <RadioButtonGroup
        value={value}
        label={labelContent}
        {...rest}
      >
        <RadioButton
          value={AllowPolicy.ALL_MEMBERS}
          disabled={disabledValues?.includes(AllowPolicy.ALL_MEMBERS)}
          label={t(
            'groups-web.group.settings.permissions-form.policy.all-members',
          )}
        />

        <RadioButton
          value={AllowPolicy.OWNER_AND_ADMINS}
          disabled={disabledValues?.includes(AllowPolicy.OWNER_AND_ADMINS)}
          label={t('groups-web.group.settings.permissions-form.policy.admins')}
        />

        {isSiteAdmin && (
          <RadioButton
            value={AllowPolicy.OWNER}
            label={t('groups-web.group.settings.permissions-form.policy.owner')}
          />
        )}
      </RadioButtonGroup>
    </>
  );
}

AllowPolicyField.displayName = 'AllowPolicyField';

function renderLabelContent(
  label: React.ReactNode,
  helperText?: string,
  currentSetting?: string,
) {
  if (!helperText && !currentSetting) {
    return label;
  }

  return (
    <Box direction="vertical" gap="SP1">
      <Typography as="div" variant="p2-14">
        {label}
      </Typography>

      {helperText && (
        <Typography as="span" variant="p2-12" secondary>
          {helperText}
        </Typography>
      )}

      {currentSetting && (
        <Typography as="span" variant="p2-16">
          {currentSetting}
        </Typography>
      )}
    </Box>
  );
}
